"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

const STRINGS: Record<
  Locale,
  {
    regions: Record<string, string>;
    funds: string[];
    actualHoldings: string;
    actual: string;
    concentration: (pct: number) => string;
    bottomNote: string;
    title: string;
    description: string;
  }
> = {
  ja: {
    regions: { us: "米国", eu: "欧州", em: "新興国", jp: "日本", other: "その他" },
    funds: ["全世界株式", "S&P500", "NASDAQ100"],
    actualHoldings: "1/3 ずつ保有したときの実質の中身",
    actual: "実質",
    concentration: (pct) => `米国に約 ${pct}% 集中`,
    bottomNote: "本数が多い = 分散 とは限らない。大切なのは「何を重ねて持っているか」",
    title: "本数ではなく、重なっている中身を見る",
    description:
      "別々の商品に見えても、中に入っている国や銘柄が近いと、実質的には同じものを厚く持っているだけになります。",
  },
  en: {
    regions: { us: "US", eu: "Europe", em: "EM", jp: "Japan", other: "Other" },
    funds: ["Global equity", "S&P 500", "NASDAQ 100"],
    actualHoldings: "What you actually hold with one third in each",
    actual: "Actual",
    concentration: (pct) => `About ${pct}% concentrated in the US`,
    bottomNote: "More funds does not mean more diversified. What matters is what you are stacking up",
    title: "Look at the overlap inside, not the number of funds",
    description:
      "Products can look separate, but if the countries and holdings inside are similar, you are simply holding more of the same thing.",
  },
};

// ── Colors ─────────────────────────────────────────────────────────

const C = {
  us: COLOR.sky,
  eu: COLOR.emerald,
  jp: COLOR.rose,
  em: "#7c3aed",
  other: COLOR.amber,
  overlap: "#dc2626",
};

// ── Fund definitions ───────────────────────────────────────────────

type Segment = { key: string; pct: number; color: string };

type Fund = {
  segments: Segment[];
};

const FUNDS: Fund[] = [
  {
    segments: [
      { key: "us", pct: 63, color: C.us },
      { key: "eu", pct: 15, color: C.eu },
      { key: "em", pct: 10, color: C.em },
      { key: "jp", pct: 5, color: C.jp },
      { key: "other", pct: 7, color: C.other },
    ],
  },
  {
    segments: [{ key: "us", pct: 100, color: C.us }],
  },
  {
    segments: [{ key: "us", pct: 100, color: C.us }],
  },
];

// Equal-weight merge (1/3 each) → actual exposure
const MERGED: Segment[] = (() => {
  const n = FUNDS.length;
  const sum: Record<string, { pct: number; color: string }> = {};
  FUNDS.forEach((f) => {
    f.segments.forEach((s) => {
      if (!sum[s.key]) sum[s.key] = { pct: 0, color: s.color };
      sum[s.key].pct += s.pct / n;
    });
  });
  const order = ["us", "eu", "em", "jp", "other"];
  return order
    .filter((k) => sum[k])
    .map((k) => ({ key: k, pct: Math.round(sum[k].pct * 10) / 10, color: sum[k].color }));
})();

// ── Primitives ─────────────────────────────────────────────────────

function StackedBar({
  x,
  y,
  w,
  h,
  segments,
  minLabelW = 28,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  segments: Segment[];
  minLabelW?: number;
}) {
  const t = STRINGS[useDocLocale()];
  let offset = 0;
  return (
    <g>
      {segments.map((seg) => {
        const sw = (seg.pct / 100) * w;
        const sx = x + offset;
        offset += sw;
        const showLabel = sw >= minLabelW;
        return (
          <g key={seg.key}>
            <rect
              x={sx}
              y={y}
              width={sw}
              height={h}
              rx={3}
              fill={seg.color}
              fillOpacity={0.22}
              stroke={seg.color}
              strokeWidth="1"
              strokeOpacity={0.55}
            />
            {showLabel ? (
              <g>
                <text
                  x={sx + sw / 2}
                  y={y + h / 2 - 3}
                  textAnchor="middle"
                  fontSize="8"
                  fill={seg.color}
                  fontWeight="600"
                >
                  {t.regions[seg.key]}
                </text>
                <text
                  x={sx + sw / 2}
                  y={y + h / 2 + 8}
                  textAnchor="middle"
                  fontSize="9"
                  fill={seg.color}
                  fontWeight="700"
                >
                  {seg.pct}%
                </text>
              </g>
            ) : null}
          </g>
        );
      })}
    </g>
  );
}

// ── Illustration ───────────────────────────────────────────────────

function OverlapIllustration() {
  const t = STRINGS[useDocLocale()];
  const VW = 460;
  const labelW = 84;
  const barX = labelW + 8;
  const barW = VW - barX - 14;
  const barH = 28;

  const topY = 18;
  const rowGap = 8;
  const fundsBlockH = FUNDS.length * barH + (FUNDS.length - 1) * rowGap;
  const arrowY = topY + fundsBlockH + 18;
  const arrowH = 22;
  const mergedY = arrowY + arrowH + 12;
  const footerY = mergedY + barH + 28;
  const VH = footerY + 10;

  const usMergedPct = MERGED.find((s) => s.key === "us")?.pct ?? 0;

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      {/* Funds (3 separate bars) */}
      {FUNDS.map((fund, i) => {
        const y = topY + i * (barH + rowGap);
        return (
          <g key={t.funds[i]!}>
            <text x={14} y={y + barH / 2 + 3} fontSize="9.5" fill="currentColor" fontWeight="700">
              {t.funds[i]!}
            </text>
            <StackedBar x={barX} y={y} w={barW} h={barH} segments={fund.segments} />
          </g>
        );
      })}

      {/* Merge arrow */}
      <g>
        <text x={VW / 2} y={arrowY + 6} textAnchor="middle" fontSize="8.5" fill="currentColor" fillOpacity={0.55}>
          {t.actualHoldings}
        </text>
        <path
          d={`M ${VW / 2 - 6} ${arrowY + 10} L ${VW / 2} ${arrowY + 18} L ${VW / 2 + 6} ${arrowY + 10}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeOpacity={0.4}
        />
      </g>

      {/* Merged (actual exposure) */}
      <text x={14} y={mergedY + barH / 2 + 3} fontSize="9.5" fill={C.overlap} fontWeight="700">
        {t.actual}
      </text>
      <StackedBar x={barX} y={mergedY} w={barW} h={barH} segments={MERGED} />

      {/* Highlight overlap region on merged bar (the US portion) */}
      <rect
        x={barX}
        y={mergedY - 3}
        width={(usMergedPct / 100) * barW}
        height={barH + 6}
        rx={4}
        fill="none"
        stroke={C.overlap}
        strokeWidth="1.4"
        strokeDasharray="4 3"
        opacity={0.75}
      />
      <text
        x={barX + ((usMergedPct / 100) * barW) / 2}
        y={mergedY + barH + 14}
        textAnchor="middle"
        fontSize="8.5"
        fill={C.overlap}
        fontWeight="700"
      >
        {t.concentration(Math.round(usMergedPct))}
      </text>

      {/* Footer message */}
      <text x={VW / 2} y={footerY} textAnchor="middle" fontSize="9.5" fill="currentColor" fontWeight="600">
        {t.bottomNote}
      </text>
    </svg>
  );
}

// ── Export ─────────────────────────────────────────────────────────

export function DocFundOverlapDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.title}
      description={t.description}
    >
      <OverlapIllustration />
    </IllustrationCard>
  );
}
